import { __assign } from 'tslib';
import React, { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { Popover } from '@alfalab/core-components-popover/esm';

var defaultColors = {"hint":"tooltip__hint_g9z7v","arrow":"tooltip__arrow_g9z7v","tooltip":"tooltip__tooltip_g9z7v"};
require('./../default.css')

var styles = {"component":"tooltip__component_v8qa3","popper":"tooltip__popper_v8qa3","hint":"tooltip__hint_v8qa3","target":"tooltip__target_v8qa3","overlap":"tooltip__overlap_v8qa3","inline":"tooltip__inline_v8qa3"};
require('./../index.css')

var invertedColors = {"tooltip":"tooltip__tooltip_1d341","arrow":"tooltip__arrow_1d341","hint":"tooltip__hint_1d341"};
require('./../inverted.css')

var desktopStyles = {"popper":"tooltip__popper_1q6ro","hint":"tooltip__hint_1q6ro"};
require('./desktop.css')

var colorStyles = {
    default: defaultColors,
    inverted: invertedColors,
};
var DEFAULT_OFFSET = [0, 16];
var TooltipDesktop = function (_a) {
    var _b;
    var children = _a.children, content = _a.content, _c = _a.trigger, trigger = _c === void 0 ? 'hover' : _c, _d = _a.onCloseDelay, onCloseDelay = _d === void 0 ? 300 : _d, _e = _a.onOpenDelay, onOpenDelay = _e === void 0 ? 300 : _e, dataTestId = _a.dataTestId, forcedOpen = _a.open, _f = _a.offset, offset = _f === void 0 ? DEFAULT_OFFSET : _f, position = _a.position, contentClassName = _a.contentClassName, arrowClassName = _a.arrowClassName, popoverClassName = _a.popoverClassName, updatePopover = _a.updatePopover, targetClassName = _a.targetClassName, _g = _a.targetTag, TargetTag = _g === void 0 ? 'div' : _g, zIndex = _a.zIndex, onClose = _a.onClose, onOpen = _a.onOpen, getPortalContainer = _a.getPortalContainer, _h = _a.view, view = _h === void 0 ? 'tooltip' : _h, _j = _a.targetRef, targetRef = _j === void 0 ? null : _j, fallbackPlacements = _a.fallbackPlacements, _k = _a.preventOverflow, preventOverflow = _k === void 0 ? true : _k, _l = _a.availableHeight, availableHeight = _l === void 0 ? false : _l, _m = _a.anchor, anchor = _m === void 0 ? null : _m, _o = _a.colors, colors = _o === void 0 ? 'default' : _o, useAnchorWidth = _a.useAnchorWidth, onTargetClick = _a.onTargetClick;
    var _p = useState(!!forcedOpen), visible = _p[0], setVisible = _p[1];
    var _q = useState(null), target = _q[0], setTarget = _q[1];
    var contentRef = useRef(null);
    var timer = useRef(0);
    var show = forcedOpen === undefined ? visible : forcedOpen;
    var open = function () {
        if (!show) {
            setVisible(true);
            if (onOpen) {
                onOpen();
            }
        }
    };
    var close = useCallback(function () {
        if (show) {
            setVisible(false);
            if (onClose) {
                onClose();
            }
        }
    }, [onClose, show]);
    var toggle = function () {
        if (show) {
            close();
        }
        else {
            open();
        }
    };
    var clickedOutside = useCallback(function (node) {
        if (target && target.contains(node)) {
            return false;
        }
        if (contentRef.current && contentRef.current.contains(node)) {
            return false;
        }
        return true;
    }, [target]);
    useEffect(function () {
        var handleBodyClick = function (event) {
            var eventTarget = event.target;
            if (clickedOutside(eventTarget)) {
                close();
            }
        };
        document.body.addEventListener('click', handleBodyClick);
        document.body.addEventListener('touchstart', handleBodyClick);
        return function () {
            document.body.removeEventListener('click', handleBodyClick);
            document.body.removeEventListener('touchstart', handleBodyClick);
            clearTimeout(timer.current);
        };
    }, [clickedOutside, close]);
    var handleTargetClick = function (event) {
        event.persist();
        onTargetClick === null || onTargetClick === void 0 ? void 0 : onTargetClick(event);
        toggle();
    };
    var handleMouseOver = function () {
        clearTimeout(timer.current);
        timer.current = window.setTimeout(function () {
            open();
        }, onOpenDelay);
    };
    var handleMouseOut = function () {
        clearTimeout(timer.current);
        timer.current = window.setTimeout(function () {
            close();
        }, onCloseDelay);
    };
    var handleTouchStart = function (event) {
        var eventTarget = event.target;
        clearTimeout(timer.current);
        if (clickedOutside(eventTarget)) {
            timer.current = window.setTimeout(function () {
                close();
            }, onCloseDelay);
        }
        else {
            open();
        }
    };
    var onClickProps = { onClick: handleTargetClick };
    var onHoverProps = {
        onMouseOver: handleMouseOver,
        onMouseOut: handleMouseOut,
        onTouchStart: handleTouchStart,
    };
    var getTargetProps = function () {
        var _a;
        var props = {
            className: cn(styles.target, targetClassName, (_a = {},
                _a[styles.inline] = TargetTag === 'span',
                _a)),
        };
        switch (trigger) {
            case 'click':
                return __assign(__assign({}, props), onClickProps);
            case 'hover':
                return __assign(__assign({}, props), onHoverProps);
            default:
                return {};
        }
    };
    var getContentProps = function () {
        var props = {
            ref: contentRef,
            'data-test-id': dataTestId,
            className: cn(styles.component, contentClassName),
        };
        switch (trigger) {
            case 'hover':
                return __assign(__assign({}, props), onHoverProps);
            default:
                return props;
        }
    };
    return (React.createElement(Fragment, null,
        React.createElement(TargetTag, __assign({ ref: mergeRefs([targetRef, setTarget]) }, getTargetProps()),
            children.props.disabled && React.createElement("div", { className: styles.overlap }),
            children),
        React.createElement(Popover, { anchorElement: anchor || target, open: show, getPortalContainer: getPortalContainer, arrowClassName: cn(arrowClassName, styles.arrow, colorStyles[colors].arrow), popperClassName: cn(styles.popper, styles[view], colorStyles[colors][view], (_b = {},
                _b[desktopStyles.popper] = view === 'tooltip',
                _b[desktopStyles.hint] = view === 'hint',
                _b)), className: popoverClassName, offset: offset, withArrow: true, position: position, update: updatePopover, zIndex: zIndex, fallbackPlacements: fallbackPlacements, preventOverflow: preventOverflow, availableHeight: availableHeight, useAnchorWidth: useAnchorWidth },
            React.createElement("div", __assign({}, getContentProps()), content))));
};

export { TooltipDesktop };
